import React from "react";

import { sampleFields } from "./guides";
import type { CardCopy, SignalDefinition, SignalTier } from "./types";
import ActionCard from "../../Pages/MyPortfolioAccounts/ActionCard";
import type { SignalCard } from "../../Store/Reducers/SignalFeedSlice";

interface CardPreviewProps {
  definition: SignalDefinition;
  /** The button label users would see. */
  label: string;
  /** Card text to show; missing fields fall back to the default text. */
  copy?: CardCopy | null;
  /** Placeholders to leave empty, to preview how a missing value reads. */
  missing?: string[];
  /** An awareness plan's user-facing line. */
  awarenessNote?: string | null;
  /** A single-action plan's guidance line, above the button. */
  detail?: string | null;
  /** Clickable field markers (the card itself stays inert). */
  markers?: React.ComponentProps<typeof ActionCard>["markers"];
  /** Parameter values for {{parameter-id}} — the draft's (see
   * draftParameterValues), so text reads as it would after Apply. */
  parameters?: Record<string, number | string>;
  /** The draft's tier (see draftTier); defaults to the catalogue tier. */
  tier?: SignalTier;
}

/**
 * The real Actions card, inert, fed example values that make this signal's
 * condition true — so what an admin reviews is what users see.
 */
const CardPreview: React.FC<CardPreviewProps> = ({
  definition,
  label,
  copy,
  missing = [],
  awarenessNote = null,
  detail = null,
  markers,
  parameters = {},
  tier,
}) => {
  const fields = { ...sampleFields(definition.id) };
  missing.forEach(name => {
    fields[name] = null;
  });
  // Card text is filled only from the fields this signal's firings carry,
  // so a placeholder the signal doesn't have reads as it would live
  // ("unknown") instead of borrowing an example value.
  const triggeredFields: Record<string, unknown> = {};
  definition.placeholders.forEach(name => {
    if (name in fields) triggeredFields[name] = fields[name];
  });
  const num = (key: string) =>
    typeof fields[key] === "number" ? (fields[key] as number) : null;
  const card: SignalCard = {
    signal_firing_id: "preview",
    account_id: "preview",
    account_name: "Example account",
    signal_key: definition.key,
    tier: tier ?? definition.tier,
    state: "active",
    first_fired_at: new Date().toISOString(),
    persona: null,
    evidence: {
      triggeredFields: {
        ...triggeredFields,
        ...(definition.variant
          ? { expansion_opportunity: definition.variant }
          : {}),
      },
      conditionString: "",
    },
    cta: {
      label: label || "—",
      kind: "synopsis",
      source: "override",
      // Proposed signals have no default card text yet; without a title
      // the card would show the raw signal id.
      card_copy: copy ?? { title: definition.title },
      awareness_note: awarenessNote,
      detail,
    },
  };
  return (
    <div
      className="pointer-events-none flex justify-center rounded-lg bg-gray-100 p-4"
      aria-hidden={!markers}
    >
      <ActionCard
        card={card}
        context={{
          al: num("al"),
          rl: num("rl"),
          cluster: null,
          renewalDays: num("renewal_days"),
          alStaleDays: num("al_stale_days"),
          ah: num("ah"),
          bhClass: null,
          bh: num("bh"),
          targetAl: null,
          rlConditions: null,
          alGap: null,
          revenue: null,
        }}
        busy={false}
        onSnooze={() => undefined}
        onDismiss={() => undefined}
        withOrg={path => path}
        onOpenAssess={() => undefined}
        onOpenBrief={() => undefined}
        markers={markers}
        parameters={parameters}
      />
    </div>
  );
};

export default CardPreview;
